import { computed, inject, provide, shallowRef } from 'vue';
import type { ComputedRef, InjectionKey, ShallowRef } from 'vue';
import type { AssistantPendingQuestion, AssistantQuestion } from '@/components/assistant/types';

export type { AssistantPendingQuestion, AssistantQuestion } from '@/components/assistant/types';

export interface AssistantChatContext {
  isPanelOpen: ShallowRef<boolean>;
  pendingQuestion: ShallowRef<AssistantPendingQuestion | null>;
  quickQuestions: ShallowRef<AssistantQuestion[]>;
  featuredQuestions: ComputedRef<AssistantQuestion[]>;
  openPanel: () => void;
  closePanel: () => void;
  askQuestion: (question: string) => void;
  clearPendingQuestion: (questionId: number) => void;
  updateQuestions: (questions: string[]) => void;
}

const AssistantChatKey: InjectionKey<AssistantChatContext> = Symbol('AssistantChat');

const createQuestion = (question: string, index: number): AssistantQuestion => {
  const normalizedQuestion = question.trim();

  return {
    id: `server-question-${index}-${normalizedQuestion}`,
    prompt: normalizedQuestion,
  };
};

const createAssistantChat = (): AssistantChatContext => {
  let pendingQuestionId = 0;

  const isPanelOpen = shallowRef(false);
  const pendingQuestion = shallowRef<AssistantPendingQuestion | null>(null);
  const quickQuestions = shallowRef<AssistantQuestion[]>([]);
  const featuredQuestions = computed(() => quickQuestions.value.slice(0, 3));

  const openPanel = () => {
    isPanelOpen.value = true;
  };

  const closePanel = () => {
    isPanelOpen.value = false;
  };

  const askQuestion = (question: string) => {
    const content = question.trim();

    if (!content) {
      return;
    }

    pendingQuestion.value = {
      id: pendingQuestionId += 1,
      content,
    };
    openPanel();
  };

  const clearPendingQuestion = (questionId: number) => {
    if (pendingQuestion.value?.id === questionId) {
      pendingQuestion.value = null;
    }
  };

  const updateQuestions = (questions: string[]) => {
    const nextQuestions = questions
      .map(createQuestion)
      .filter(question => question.prompt);

    quickQuestions.value = nextQuestions;
  };

  return {
    isPanelOpen,
    pendingQuestion,
    quickQuestions,
    featuredQuestions,
    openPanel,
    closePanel,
    askQuestion,
    clearPendingQuestion,
    updateQuestions,
  };
};

export const provideAssistantChat = () => {
  const assistantChat = createAssistantChat();

  provide(AssistantChatKey, assistantChat);

  return assistantChat;
};

export const useAssistantChat = () => {
  const assistantChat = inject(AssistantChatKey);

  if (!assistantChat) {
    throw new Error('useAssistantChat must be used after provideAssistantChat');
  }

  return assistantChat;
};
